// src/pages/HeroSection.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  SiGithub,
  SiLinkedin,
  SiX  ,
  SiReact,
  SiPostgresql,
  SiTypescript,
  SiThreedotjs,
  SiNodedotjs,
} from "react-icons/si";
import { Mail, FileText, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Badge = ({ icon: Icon, label, href, color }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="
      inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md 
      bg-gray-200/60 dark:bg-gray-800/40 
      border border-gray-300 dark:border-gray-700
      text-gray-700 dark:text-gray-200 
      text-xs sm:text-sm hover:bg-gray-300/60 dark:hover:bg-gray-700/40 
      transition cursor-pointer
    "
  >
    <Icon size={14} className={color} />
    {label}
  </a>
);

const SocialIcon = ({ icon: Icon, label, href }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      className="relative flex items-center justify-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 dark:text-gray-400 hover:text-gray-500 dark:hover:text-lightwhite-400 transition"
      >
        <Icon size={20} className="sm:size-22" />
      </a>

      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: -6 }}
            exit={{ opacity: 0, scale: 0.85, y: 8 }}
            transition={{ duration: 0.18 }}
            className="
              absolute -top-7 px-2 py-1 rounded-md 
              bg-gray-900/80 dark:bg-black/80
              text-white text-[10px] sm:text-xs 
              shadow-lg border border-white/10 
              backdrop-blur-sm whitespace-nowrap
            "
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();
  return (
    <motion.section
      className="relative max-w-4xl mx-auto mt-16 sm:mt-20 px-4"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeInUp}
    >
      {/* 🔥 AVATAR */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex justify-start mb-2 cursor-pointer group"
      >
        <motion.div
          className="relative rounded-full p-1"
        >
          {/* Avatar */}
          <img
            src="/avatar.png"
            alt="Avatar"
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover shadow-md"
          />

          {/* Border ring (glow on hover) */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-black/50 dark:border-white/30 pointer-events-none"
            whileHover={{ boxShadow: "0 0 12px rgba(0,255,120,0.45)" }}
            transition={{ duration: 0.3 }}
          />

          {/* Status dot (pulse animation) */}
          <motion.div
            className="
        absolute bottom-2 right-2
        w-5 h-5 rounded-full bg-green-400
        border-[3px] border-black dark:border-gray-900 shadow-md
      "
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />

          {/* Hover card (smooth fade + slide) */}
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 4 }}
              whileInView={{ opacity: 1 }}
              whileHover={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 4 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="
          absolute
          hidden group-hover:flex flex-col gap-1
          bg-white/90 dark:bg-black/60 backdrop-blur-xl
          px-4 py-3 w-64 rounded-2xl shadow-2xl
          border border-black/10 dark:border-white/10

          /* Mobile */
          top-full left-1/2 -translate-x-1/2 mt-3

          /* Desktop */
          sm:top-1/2 sm:left-full sm:-translate-y-1/2 sm:ml-4 sm:mt-0
        "
            >
              {/* Title row */}
              <div className="flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                Open to Opportunities
              </div>

              <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">
                Recent Graduate · Building real-world experience
              </p>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* TITLE */}
      <motion.h1
        className="
          text-3xl sm:text-4xl md:text-5xl lg:text-6xl 
          font-bold text-gray-900 dark:text-white 
          leading-snug sm:leading-tight
        "
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Hi, I'm Ashish —{" "}
        <span className="text-gray-700 dark:text-gray-300 font-semibold">
          A Full Stack Web Developer.
        </span>
      </motion.h1>

      {/* DESCRIPTION */}
      <motion.p
        className="
          text-gray-700 dark:text-gray-400 
          text-base sm:text-lg mt-4 sm:mt-5 
          leading-relaxed space-y-1
        "
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        I build interactive web apps using{" "}
        <Badge
          icon={SiTypescript}
          label="Typescript"
          href="https://www.typescriptlang.org/"
          color="text-blue-500"
        />
        ,{" "}
        <Badge
          icon={SiReact}
          label="React"
          href="https://react.dev/"
          color="text-sky-500"
        />
        ,{" "}
        <Badge
          icon={SiNodedotjs}
          label="Node.js"
          href="https://nodejs.org/"
          color="text-green-600"
        />
        , and{" "}
        <Badge
          icon={SiPostgresql}
          label="PostgreSQL"
          href="https://www.postgresql.org/"
          color="text-blue-700"
        />
        . With a focus on{" "}
        <span className="text-gray-900 dark:text-white font-medium">UI</span>{" "}
        design. Enthusiastic about{" "}
        <Badge
          icon={SiThreedotjs}
          label="Three.js"
          href="https://threejs.org/"
          color="text-gray-800 dark:text-white"
        />
        , driven by a keen eye for design.
      </motion.p>

      {/* BUTTONS */}
      <div className="mt-8 flex flex-wrap gap-3 sm:gap-4">
        <button
          onClick={() => navigate("/resume")}
          className="px-5 sm:px-6 py-2.5 flex items-center gap-2 text-sm sm:text-base 
            rounded-xl border border-gray-900 dark:border-gray-200
            bg-white dark:bg-gray-900
            text-gray-900 dark:text-white
            font-medium shadow hover:shadow-lg hover:-translate-y-1 transition"
        >
          <FileText size={18} />
          Resume / CV
        </button>

        <button
          onClick={() => navigate("/contact")}
          className="
            px-5 sm:px-6 py-2.5 flex items-center gap-2 text-sm sm:text-base 
            rounded-xl 
            bg-black dark:bg-white
            text-white dark:text-black
            font-medium shadow hover:shadow-lg hover:-translate-y-1 transition
          "
        >
          <Send size={18} />
          Get in touch
        </button>
      </div>

      {/* SOCIAL ICONS */}
      <div className="mt-10 flex gap-5 sm:gap-6 flex-wrap sm:flex-nowrap">
        <SocialIcon
          icon={SiGithub}
          label="GitHub"
          href="https://github.com/ashishkarche"
        />
        <SocialIcon
          icon={SiLinkedin}
          label="LinkedIn"
          href="https://www.linkedin.com/in/ashish-karche-1a422b317/"
        />
        <SocialIcon
          icon={Mail}
          label="Email"
          href="mailto:ashishkarche9@gmail.com"
        />
        <SocialIcon
          icon={SiX}
          label="Twitter"
          href="https://x.com/"
        />
      </div>
    </motion.section>
  );
};

export default HeroSection;
